/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Canvas2D Fallback Render Backend
   Universal CPU fallback for environments lacking hardware acceleration.
   ═══════════════════════════════════════════════════════════════ */

import type {
  RenderBackend,
  ShaderPassDescriptor,
  TextureHandle,
} from '../types';
import { generateStableId } from '../../domain/id';

export class Canvas2DBackend implements RenderBackend {
  readonly name = 'canvas2d' as const;
  readonly isAvailable: boolean;

  constructor() {
    this.isAvailable = typeof document !== 'undefined';
  }

  createTexture(
    source: ImageBitmap | HTMLImageElement | HTMLCanvasElement | ImageData,
    width?: number,
    height?: number
  ): TextureHandle {
    const w = width ?? (source instanceof ImageData ? source.width : source.width);
    const h = height ?? (source instanceof ImageData ? source.height : source.height);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;

    if (source instanceof ImageData) {
      ctx.putImageData(source, 0, 0);
    } else {
      ctx.drawImage(source, 0, 0);
    }

    return {
      id: generateStableId('node'),
      width: w,
      height: h,
      nativeHandle: canvas,
      dispose: () => {
        canvas.width = 0;
        canvas.height = 0;
      },
    };
  }

  async executeShaderPass(
    _descriptor: ShaderPassDescriptor,
    _outputWidth: number,
    _outputHeight: number
  ): Promise<TextureHandle> {
    throw new Error('GPU shader passes are not supported on Canvas2D. Hardware-accelerated WebGL2 backend is required.');
  }

  renderToCanvas(texture: TextureHandle, targetCanvas: HTMLCanvasElement, maxDimension?: number): void {
    let targetWidth = texture.width;
    let targetHeight = texture.height;

    if (maxDimension && maxDimension > 0) {
      const maxSide = Math.max(texture.width, texture.height);
      if (maxSide > maxDimension) {
        const scale = maxDimension / maxSide;
        targetWidth = Math.max(1, Math.round(texture.width * scale));
        targetHeight = Math.max(1, Math.round(texture.height * scale));
      }
    }

    targetCanvas.width = targetWidth;
    targetCanvas.height = targetHeight;
    const ctx = targetCanvas.getContext('2d');
    if (ctx && texture.nativeHandle instanceof HTMLCanvasElement) {
      ctx.drawImage(texture.nativeHandle, 0, 0, targetWidth, targetHeight);
    }
  }

  async readPixels(texture: TextureHandle): Promise<Uint8ClampedArray> {
    if (texture.nativeHandle instanceof HTMLCanvasElement) {
      const ctx = texture.nativeHandle.getContext('2d');
      if (ctx) {
        return ctx.getImageData(0, 0, texture.width, texture.height).data;
      }
    }
    return new Uint8ClampedArray(texture.width * texture.height * 4);
  }

  dispose(): void {}
}
